import L, { nonTiledLayer, tileLayer, TileLayer, type Layer } from 'leaflet'
import { useMemo } from 'react'
import MAP_CONFIG from '../legacy/services/map.config'
import { getAccessToken, isAuthenticated } from '../../../shared/utils/auth/auth'
import useParam from '../../../shared/hooks/useParam'
import { baseLayerParam } from '../query-params'
import { getMapBaseLayer } from '../../../shared/utils/map'
import 'leaflet.nontiledlayer'
import { DEFAULT_MAP_LAYER_URL } from '../../../shared/components/LeafletMap/LeafletMap'
import type { TmsOverlay, WmsOverlay } from '../../../shared/contexts/Layers/LayerProvider'
import useLayerContext from '../../../shared/contexts/Layers/useLayerContext'
import { useMapContext } from '../../../shared/contexts/map/MapContext'

const useLayers = () => {
  const [baseLayerId] = useParam(baseLayerParam)
  const { featureLayers } = useMapContext()
  const { legendLeafletLayers } = useLayerContext()

  const baseLayer = useMemo(() => {
    const { urlTemplate } = getMapBaseLayer(baseLayerId) ?? {}

    return L.tileLayer(urlTemplate ?? DEFAULT_MAP_LAYER_URL, {
      id: `base-${baseLayerId}`,
      attribution: '',
      subdomains: MAP_CONFIG.BASE_LAYER_OPTIONS.subdomains,
      tms: true,
    })
  }, [baseLayerId])

  // TMS is only historische kaarten layers
  const tmsLayers = useMemo(
    () =>
      legendLeafletLayers
        .filter((overlay): overlay is TmsOverlay => overlay.type === 'tms')
        .map(({ url, options, id }) =>
          tileLayer(url, {
            ...MAP_CONFIG.BASE_LAYER_OPTIONS,
            ...options,
            id,
          }),
        ),
    [legendLeafletLayers],
  )

  const wmsLayers = useMemo(
    () =>
      legendLeafletLayers
        .filter((overlay): overlay is WmsOverlay => overlay.type === 'wms')
        .map(({ layer, url, options, id, params }) => {
          const searchParams = new URLSearchParams(params)
          if (layer.authScope && isAuthenticated()) {
            searchParams.set('access_token', getAccessToken())
          }

          const layerUrl = searchParams ? `${url}?${searchParams.toString()}` : url

          return nonTiledLayer.wms(layerUrl, { ...options, id }) as TileLayer
        }),
    [legendLeafletLayers],
  )

  return useMemo<Layer[]>(
    () => [baseLayer, ...tmsLayers, ...wmsLayers, ...(featureLayers ?? [])].filter(Boolean),
    [baseLayer, tmsLayers, wmsLayers, featureLayers],
  )
}

export default useLayers
